import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    userInfo: null,
    accessToken: null,
    idToken: null,
    refreshToken: null,
    userId: null,
    tenantInfo: null,
    permissions: [],
    isPlatformRole: false,
    isLoggedIn: false,
} as {
    userInfo: null | any;
    accessToken: null | string;
    idToken: null | string;
    refreshToken: null | string;
    userId: null | string;
    tenantInfo: null | any;
    permissions: any[];
    isPlatformRole: boolean;
    isLoggedIn: boolean;
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCredentials: (state, action) => {
            const { user, accessToken, idToken, refreshToken } = action.payload;
            state.userInfo = user;
            state.accessToken = accessToken;
            state.idToken = idToken;
            state.refreshToken = refreshToken;
        },
        setAuthInfo: (state, action) => {
            const { userInfo, accessToken, idToken, refreshToken, userId, tenantInfo, isPlatformRole } = action.payload;
            state.userInfo = userInfo;
            state.accessToken = accessToken;
            state.idToken = idToken;
            state.refreshToken = refreshToken;
            state.userId = userId;
            // Backend often returns `tenantId`; UI and APIs expect a stable `id` field.
            state.tenantInfo = tenantInfo
                ? { ...tenantInfo, id: tenantInfo.id ?? tenantInfo.tenantId }
                : null;
            state.isPlatformRole = isPlatformRole;
        },
        setLogin: (state, action) => {
            state.isLoggedIn = action.payload;
        },
        setAuthTokens: (state, action) => {
            const { idToken, accessToken, refreshToken } = action.payload;
            state.idToken = idToken;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
        },
        setPermissions: (state, action) => {
            state.permissions = action.payload ?? [];
        },
        logOut: (state, action) => {
            return initialState;
        }
    }
});

export const { setCredentials, setAuthInfo, setLogin, setAuthTokens, setPermissions, logOut } = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: any) => state.auth.userInfo;
export const selectCurrentToken = (state: any) => state.auth.accessToken;
export const selectCurrentRefreshToken = (state: any) => state.auth.refreshToken;
export const getAuthTokens = (state: any) => ({
    accessToken: state.auth.accessToken,
    idToken: state.auth.idToken,
    refreshToken: state.auth.refreshToken,
});
export const getTenantInfo = (state: any) => state.auth.tenantInfo;
/** Resolves tenant UUID whether the API stored it as `id` or `tenantId`. */
export const getTenantId = (state: any): string => {
    const t = state?.auth?.tenantInfo;
    if (!t) return "";
    const id = t.id ?? t.tenantId;
    return id != null && String(id).trim() !== "" ? String(id) : "";
};
export const getIsLoggedIn = (state: any) => state.auth.isLoggedIn;
export const getPermissionsList = (state: any) => state.auth.permissions ?? [];